import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import { Button } from 'foundations-components/transitional/components/Button';
import { FloatingAlertList, FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { DetailsPageContext } from 'marketplace-ui-common/context/DetailsPageContext';
import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import { useListingPreview } from 'marketplace-ui-common/hooks/useListingPreview';
import { useUnifiedDetails } from 'marketplace-ui-common/hooks/useUnifiedDetails';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import { useTracking } from 'marketplace-ui-install/hooks/useTracking';
import { ReviewFlowProvider } from 'marketplace-ui-reviews/contexts/ReviewFlowContext';
import { getReviewTypeFromProductType } from 'marketplace-ui-reviews/utils/getReviewTypeFromProductType';
import { useLocation, useRouteMatch } from 'quartz-routing/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCreateLeadContactMutation } from '../__generated__/chirp/com/hubspot/marketplace/analytics/rpc/LeadGenerationRpc.hooks';
import { PlanCombinationsModal } from './compatibility/PlanCombinationsModal';
import { DetailsError } from './error/DetailsError';
import { useAppReleaseHistory } from './hooks/useAppReleaseHistory';
import { DetailsPage } from './layout/DetailsPage';
import { ProductBuyBox } from './layout/cards/ProductBuyBox';
import { ProductFeaturesCard } from './layout/cards/ProductFeaturesCard';
import { ProductFeedbackCard } from './layout/cards/ProductFeedbackCard';
import { ProductFunctionalityCard } from './layout/cards/ProductFunctionalityCard';
import { ProductInfoCard } from './layout/cards/ProductInfoCard';
import { ProductOverviewCard } from './layout/cards/ProductOverviewCard';
import { ProductPricingCard } from './layout/cards/ProductPricingCard';
import { ProductRequirementsCard } from './layout/cards/ProductRequirementsCard';
import { ProductReviewsCard } from './layout/cards/ProductReviewsCard';
import { HSPageViewTracker } from './utils/HSPageViewTracker';

// @ts-ignore ts-migrate(2307) FIXME: Cannot find module 'bender-url!ecosystem-marketpla... Remove this comment to see the full error message
import fallbackAgentOverviewImage from 'bender-url!marketplace-ui-details/img/agent-overview-fallback.png';
import { getAppDetailsShortlink, getSolutionsPartnerDetailsShortlink } from 'marketplace-ui-common/utils/DetailsPageUrls';
import { useInstallability } from 'marketplace-ui-install/hooks/useInstallability';
import { MarketplaceListingCTAButton } from 'marketplace-ui-install/install/MarketplaceListingCTAButton';
import { useGetRequiredScopeGroupsQuery } from '../__generated__/chirp/com/hubspot/marketplace/installs/rpc/MarketplaceInstallsRpc.hooks';
import { useGetStandardizedInstallTypeQuery } from '../__generated__/chirp/com/hubspot/marketplace/listing/details/rpc/StandardizedInstallTypeRpc.hooks';
import { McpServerConnectAlert } from './functionality/McpServerConnectAlert';
import { AppGovernanceAlert } from './governance/AppGovernanceAlert';
import { AgentUpgradeBanner } from './install/AgentUpgradeBanner';
import { ProductInfoList } from './layout/cards/ProductInfoList';
import { ProductPartnerTierCard } from './layout/cards/ProductPartnerTierCard';
import { buildSolutionsPartnerProfileInfoListSections } from './utils/productInfoListUtils';
import { setWindowLocationHref } from 'hs-browser-apis/location';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function Details({
  appContext,
  logInUrl,
  // To be used in the Public Marketplace when the CTA reads "Sign in"
  previewListingId,
  // Will be used to fetch listing preview data
  language,
  renderedErrorLink,
  slug,
  userInfo,
  isEmbedded = false,
  onEmbedClose,
  onInstallComplete,
  onInstallError
}) {
  var _releases$, _installTypeData$inst, _user;
  const {
    path
  } = useRouteMatch();
  const isReviewLink = path.includes('listing/:listingSlug/write-review') || path.includes('solutions/:categorySlug/write-review');
  const isSolutionsPartnerProfileUrl = slug === null || slug === void 0 ? void 0 : slug.startsWith('profile:');
  useEffect(() => {
    if (isReviewLink && slug && appContext !== 'app') {
      const detailsShortlink = isSolutionsPartnerProfileUrl ? getSolutionsPartnerDetailsShortlink(slug.replace(/^profile:/, '')) : getAppDetailsShortlink(slug);
      setWindowLocationHref(`${detailsShortlink}/write-review`);
    }
  }, [isReviewLink, appContext, slug, isSolutionsPartnerProfileUrl]);
  const published = !!slug && !previewListingId;
  const isPreview = !!previewListingId;
  const [compatibilityModalOpen, setCompatibilityModalOpen] = useState(false);
  const {
    pathname
  } = useLocation();

  // This will be the only call to Unified Details Service
  // Once everything is migrated to V3
  const {
    unifiedDetailsData: unifiedDetailsDataV3,
    unifiedDetailsLoading: unifiedDetailsLoadingV3,
    unifiedDetailsError: unifiedDetailsErrorV3
  } = useUnifiedDetails({
    appContext,
    slug: slug || '',
    language: getLang(),
    skip: !slug || isPreview,
    version: 3
  });

  // Additional call to Unified Details Service V2
  // To get ids from the legacy system
  const {
    unifiedDetailsData: unifiedDetailsDataV2
  } = useUnifiedDetails({
    appContext,
    slug: slug || '',
    language: getLang(),
    skip: !slug || isPreview || isSolutionsPartnerProfileUrl,
    version: 2
  });
  const {
    previewData,
    previewLoading,
    previewError
  } = useListingPreview({
    appContext,
    language: language || getLang(),
    listingId: previewListingId,
    skip: !!slug || !isPreview
  });
  const unifiedDetailsRequestStatusV3 = getDataFetchingClientRequestState(unifiedDetailsErrorV3, unifiedDetailsLoadingV3);
  const previewRequestStatus = getDataFetchingClientRequestState(previewError, previewLoading);

  // UDS V3 data from Framework
  const {
    actionCount,
    agencyPricingUrl,
    agentType,
    betaDetails,
    budgetChoice: budget,
    builtByHubSpot,
    builtInWorkflowActionTypes,
    caseStudyUrl,
    category,
    categoryLabels,
    certifiedAt,
    companyName,
    companyUrl,
    contactForm,
    credentials,
    currency,
    description,
    displayTag,
    enrollmentTrigger,
    facebookUrl,
    features,
    firstAction,
    installCount,
    installUrl,
    isUserLevel,
    languages,
    listingIcon,
    listingId,
    liveChatUrl,
    meetingsUrl,
    name,
    objective,
    objectiveLabels,
    overview,
    industryChoice: industries,
    isBreezeConnector,
    isMcpConnector,
    isMcpServer,
    mcpFaqs,
    mcpTools,
    mcpUseCases,
    offeringId,
    partnerPortalId,
    partnerTier,
    partnerTierLabel,
    partnerType,
    phone,
    physicalOfficeLocations,
    pricingPlans,
    pricingPlansUrl,
    privacyUrl1,
    privacyUrl2,
    privacyUrl3,
    productFeatures: frameworkProductFeatures,
    productType,
    profileId,
    regionChoice: regions,
    releaseTag,
    remoteLocations,
    requiredForInstallListingIds,
    requiredPostInstallListingIds,
    setupRequirements,
    screenshots: frameworkScreenshots,
    secondAction,
    services,
    setupDocsUrl,
    sharedDataInfo,
    sharedDataHow,
    sharedObjects,
    sourceId,
    supportEmail,
    supportLanguages,
    supportUrl,
    tagline,
    termsUrl,
    tier,
    ubbInfo,
    urlSlug,
    lastPublishedAt,
    workflowTemplates,
    worksWithBreeze,
    videos: frameworkVideos
  } = previewData || unifiedDetailsDataV3;
  const mcpServerData = useMemo(() => {
    if (!isMcpServer || !mcpUseCases) {
      return undefined;
    }
    return {
      mcpFaqs,
      mcpTools,
      mcpUseCases
    };
  }, [isMcpServer, mcpFaqs, mcpTools, mcpUseCases]);

  // Remaining App Details data from Unified Details Service V2
  const {
    id
  } = previewData || unifiedDetailsDataV2;
  const reviewableId = offeringId || profileId;

  // TODO: get these flags from UDS in future
  const isPublicallyReviewableProduct = productType !== 'AGENT' && productType !== 'WORKFLOW_TEMPLATE';
  const isPrivatelyReviewableProduct = productType === 'AGENT' || productType === 'WORKFLOW_TEMPLATE';
  let iconsListSections;
  switch (productType) {
    case 'SOLUTIONS_PARTNER_PROFILE':
      {
        iconsListSections = buildSolutionsPartnerProfileInfoListSections({
          budget,
          credentials,
          industries,
          languages,
          regions,
          services
        });
        break;
      }
    default:
      iconsListSections = undefined;
  }
  const [createLeadContact] = useCreateLeadContactMutation();
  const screenshots = useMemo(() => {
    // Filter out screenshot objects with empty values
    const screenshotsWithValues = frameworkScreenshots === null || frameworkScreenshots === void 0 ? void 0 : frameworkScreenshots.filter(s => s.screenshot.value);
    return screenshotsWithValues !== null && screenshotsWithValues !== void 0 && screenshotsWithValues.length ? screenshotsWithValues :
    // Default to agent fallback image
    productType === 'AGENT' ? [{
      screenshot: {
        value: fallbackAgentOverviewImage,
        altText: ''
      }
    }] : [];
  }, [frameworkScreenshots, productType]);
  const videos = useMemo(() => {
    // Filter out video objects with empty values
    return frameworkVideos === null || frameworkVideos === void 0 ? void 0 : frameworkVideos.filter(s => s.video.value);
  }, [frameworkVideos]);
  const filteredFeatures = useMemo(() => {
    // Filter out feature objects with empty values
    return features === null || features === void 0 ? void 0 : features.filter(feature => feature.featureName);
  }, [features]);
  const tools = useMemo(() => {
    return frameworkProductFeatures === null || frameworkProductFeatures === void 0 ? void 0 : frameworkProductFeatures.filter(feature => feature.data.isTool);
  }, [frameworkProductFeatures]);
  const workflowActions = useMemo(() => {
    return frameworkProductFeatures === null || frameworkProductFeatures === void 0 ? void 0 : frameworkProductFeatures.filter(feature => feature.type === 'WORKFLOW_ACTION');
  }, [frameworkProductFeatures]);
  const appObjects = useMemo(() => {
    return frameworkProductFeatures === null || frameworkProductFeatures === void 0 ? void 0 : frameworkProductFeatures.filter(feature => feature.type === 'APP_OBJECT');
  }, [frameworkProductFeatures]);
  const appCards = useMemo(() => {
    return frameworkProductFeatures === null || frameworkProductFeatures === void 0 ? void 0 : frameworkProductFeatures.filter(feature => feature.type === 'UIE' &&
    // if any of these fields are missing, don't render the card
    feature.name && feature.data.description && feature.data.previewImage && feature.data.eligibleLocations);
  }, [frameworkProductFeatures]);
  const isReleaseManagementApp = releaseTag !== undefined;
  const releaseHistoryData = useAppReleaseHistory({
    appContext,
    slug: slug || '',
    skip: !isReleaseManagementApp || !slug || isPreview || productType !== 'APP'
  });
  const releases = (releaseHistoryData === null || releaseHistoryData === void 0 ? void 0 : releaseHistoryData.releases) || [];
  const currentVersion = (_releases$ = releases[0]) === null || _releases$ === void 0 ? void 0 : _releases$.releaseTag;
  const {
    data: installTypeData
  } = useGetStandardizedInstallTypeQuery({
    variables: {
      listingId: previewListingId || listingId,
      published
    },
    skip: !listingId && !previewListingId || appContext !== 'app'
  });
  const appInstallType = (_installTypeData$inst = installTypeData === null || installTypeData === void 0 ? void 0 : installTypeData.installType) !== null && _installTypeData$inst !== void 0 ? _installTypeData$inst : 'NONE';
  const {
    data: installabilityData,
    loading: installabilityLoading
  } = useInstallability({
    appContext,
    listingId,
    published,
    skip: !listingId || isPreview || productType === 'SOLUTIONS_PARTNER_PROFILE'
  });
  const {
    data: requiredScopeGroupsData
  } = useGetRequiredScopeGroupsQuery({
    variables: {
      listingId: listingId
    },
    skip: !listingId || isPreview || productType !== 'APP'
  });
  const neededScopeGroups = useMemo(() => {
    return requiredScopeGroupsData === null || requiredScopeGroupsData === void 0 ? void 0 : requiredScopeGroupsData.scopeGroups;
  }, [requiredScopeGroupsData]);
  const installed = useMemo(() => (installabilityData === null || installabilityData === void 0 ? void 0 : installabilityData.installed) || false, [installabilityData]);
  const {
    trackInstall,
    trackInteraction,
    trackPageViewed
  } = useTracking({
    appContext,
    companyName,
    embed: isEmbedded,
    installed,
    isPreview,
    isReviewLink,
    listingId,
    slug: isEmbedded ? slug : urlSlug,
    sourceId,
    type: productType
  });
  const pageViewTracked = useRef(false);
  useEffect(() => {
    pageViewTracked.current = false;
    const floatingAlerts = FloatingAlertStore.getAlerts();
    Object.keys(floatingAlerts).forEach(alertId => {
      FloatingAlertStore.removeAlert(alertId);
    });
  }, [slug]);
  useEffect(() => {
    if (!installabilityLoading && !pageViewTracked.current && !unifiedDetailsLoadingV3) {
      trackPageViewed();
      pageViewTracked.current = true;
    }
  }, [installabilityLoading, installed, isEmbedded, pageViewTracked, trackPageViewed, unifiedDetailsLoadingV3]);
  const runLeadGen = useCallback(({
    action
  }) => {
    if (productType !== 'APP' || appContext !== 'app' || !published) {
      return;
    }
    trackInteraction({
      action: 'generated lead',
      value: `${partnerPortalId}`
    });
    if (action && listingId) {
      createLeadContact({
        variables: {
          listingId,
          action
        }
      }).catch(error => {
        console.error('Error sending lead:', error);
      });
    }
  }, [appContext, createLeadContact, listingId, partnerPortalId, productType, published, trackInteraction]);
  const isFeaturedAgent = agentType === 'OFF_PLATFORM';
  const renderedPrimaryCTA = useMemo(() => {
    // Public "Sign in" buttons that redirect to portal picker
    // TODO: Replace productType check with `installable` flag from UDS when available
    if (appContext === 'public' && productType !== 'SOLUTIONS_PARTNER_PROFILE') {
      return /*#__PURE__*/_jsx(Button, {
        use: "primary",
        href: isFeaturedAgent && installUrl ? installUrl : logInUrl,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.productDetailsCard.cta.signIn.${productType}`
        })
      });
    }
    return /*#__PURE__*/_jsx(MarketplaceListingCTAButton, {
      appContext: appContext,
      buttonProps: {
        onEmbedClose,
        onInstallComplete,
        onInstallError
      },
      isEmbedded: isEmbedded,
      inDetailsPageContext: true,
      previewListingId: previewListingId,
      slug: isSolutionsPartnerProfileUrl ? `profile:${urlSlug}` : urlSlug,
      userInfo: userInfo
    });
  }, [appContext, installUrl, isEmbedded, isFeaturedAgent, isSolutionsPartnerProfileUrl, logInUrl, onEmbedClose, onInstallComplete, onInstallError, previewListingId, productType, urlSlug, userInfo]);
  if (unifiedDetailsErrorV3) {
    return /*#__PURE__*/_jsx(DetailsError, {
      renderedErrorLink: renderedErrorLink,
      unifiedDetailsError: unifiedDetailsErrorV3
    });
  }
  return /*#__PURE__*/_jsx(LoadingWrapper, {
    requestStatus: [unifiedDetailsRequestStatusV3, previewRequestStatus],
    children: /*#__PURE__*/_jsx(DetailsPageContext.Provider, {
      value: {
        appContext,
        displayTag,
        iconUrl: listingIcon === null || listingIcon === void 0 ? void 0 : listingIcon.value,
        listingId,
        listingIdV2: id,
        // TODO - deprecate --- this is still needed for sendAdminEmail
        previewListingId,
        name,
        productType,
        published,
        runLeadGen,
        requiredForInstallListingIds,
        trackInteraction,
        trackInstall,
        userInfo,
        isEmbedded,
        isBreezeConnector,
        isMcpServer,
        isMcpConnector,
        worksWithBreeze
      },
      children: /*#__PURE__*/_jsxs(ReviewFlowProvider, {
        appContext: appContext,
        entityId: reviewableId || 0,
        entityPortalId: partnerPortalId,
        isPreview: isPreview,
        reviewType: getReviewTypeFromProductType(productType),
        reviewerUserId: userInfo === null || userInfo === void 0 || (_user = userInfo.user) === null || _user === void 0 ? void 0 : _user.user_id
        // TODO: Use a different `isReviewable` flag from UDS instead of checking the product type
        ,
        skip: !isPublicallyReviewableProduct,
        children: [/*#__PURE__*/_jsx(FloatingAlertList, {}), /*#__PURE__*/_jsx(HSPageViewTracker, {
          pathname: pathname,
          overrideTrack: appContext === 'app',
          addLang: appContext === 'app'
        }), productType === 'APP' && appContext === 'app' && /*#__PURE__*/_jsx(AppGovernanceAlert, {
          appId: parseInt(sourceId, 10),
          appName: name
        }), /*#__PURE__*/_jsx(DetailsPage, {
          leftColumn: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(ProductBuyBox, {
              agentType: agentType,
              betaDetails: betaDetails,
              certifiedAt: certifiedAt,
              companyName: companyName || 'HubSpot',
              companyUrl: companyUrl,
              contactForm: contactForm,
              installCount: installCount,
              isReleaseManagementApp: isReleaseManagementApp,
              meetingsUrl: meetingsUrl,
              tagline: tagline,
              partnerType: partnerType,
              physicalOfficeLocations: physicalOfficeLocations,
              primaryCTA: renderedPrimaryCTA,
              productId: offeringId || profileId || 0,
              remoteLocations: remoteLocations,
              sourceId: sourceId,
              tier: tier,
              builtInWorkflowActionTypes: builtInWorkflowActionTypes,
              workflowActionFeatures: workflowActions
            }), /*#__PURE__*/_jsx(ProductPartnerTierCard, {
              partnerTier: partnerTier,
              partnerTierLabel: partnerTierLabel
            }), (productType === 'APP' || requiredPostInstallListingIds) &&
            /*#__PURE__*/
            //TODO: introduce concept of "compatibilityEligible" prop on schema?
            _jsx(ProductRequirementsCard, {
              installType: appInstallType,
              features: filteredFeatures || [],
              builtByHubSpot: builtByHubSpot,
              isUserLevel: isUserLevel,
              neededScopeGroups: neededScopeGroups,
              onCompatibilityModalOpen: () => setCompatibilityModalOpen(true),
              pricingPlans: pricingPlans || [],
              requiredListingIds: requiredPostInstallListingIds,
              currentVersion: currentVersion
            }), iconsListSections && /*#__PURE__*/_jsx(ProductInfoList, {
              sections: iconsListSections
            })]
          }),
          rightColumn: /*#__PURE__*/_jsxs(_Fragment, {
            children: [isMcpServer && appContext === 'app' && /*#__PURE__*/_jsx(McpServerConnectAlert, {}), productType === 'AGENT' && appContext === 'app' && listingId && /*#__PURE__*/_jsx(AgentUpgradeBanner, {
              appContext: appContext,
              listingId: listingId,
              listingName: name !== null && name !== void 0 ? name : '',
              productType: productType,
              published: published
            }), /*#__PURE__*/_jsx(ProductOverviewCard, {
              caseStudyUrl: caseStudyUrl,
              isPreview: isPreview
              // TODO: Remove `description` once SOLUTIONS_PARTNER_PROFILE listings use `overview` instead
              ,
              overview: overview || description,
              reviewableId: reviewableId,
              screenshots: screenshots,
              setupDocsUrl: setupDocsUrl,
              videos: videos,
              enrollmentTrigger: enrollmentTrigger,
              firstAction: firstAction,
              secondAction: secondAction,
              totalActions: actionCount,
              setupRequirements: setupRequirements
            }), /*#__PURE__*/_jsx(ProductFeaturesCard, {
              features: filteredFeatures || [],
              onCompatibilityModalOpen: () => setCompatibilityModalOpen(true)
            }), /*#__PURE__*/_jsx(ProductFunctionalityCard, {
              automationActions: productType === 'APP' ? workflowActions : [],
              automationListings: workflowTemplates,
              mcpServer: mcpServerData,
              sharedDataHow: sharedDataHow,
              sharedDataInfo: sharedDataInfo,
              sharedObjects: sharedObjects,
              appObjects: appObjects,
              tools: tools,
              appCards: appCards
            }), isPublicallyReviewableProduct && reviewableId && /*#__PURE__*/_jsx(ProductReviewsCard, {
              companyName: companyName,
              isPreview: isPreview,
              isReviewLink: isReviewLink,
              productId: reviewableId,
              slug: urlSlug,
              userInfo: userInfo
            }), /*#__PURE__*/_jsx(ProductPricingCard, {
              agencyPricingUrl: agencyPricingUrl,
              currency: currency,
              pricingPlansUrl: pricingPlansUrl,
              pricingPlans: pricingPlans || [],
              ubbInfo: ubbInfo,
              pricingUpdatedAt: lastPublishedAt //TODO: replace with pricing-specific updatedAt when available from UDSV3
            }), productType !== 'SOLUTIONS_PARTNER_PROFILE' && /*#__PURE__*/_jsx(ProductInfoCard, {
              details: {
                companyName: companyName || 'HubSpot',
                companyUrl,
                category,
                categoryLabels,
                objective,
                objectiveLabels,
                languages,
                installCount
              },
              support: {
                liveChatUrl,
                supportEmail,
                facebookUrl,
                builtByHubSpot,
                phone,
                supportLanguages,
                supportUrl
              },
              legal: {
                builtByHubSpot,
                privacyUrl1,
                privacyUrl2,
                privacyUrl3,
                termsUrl
              },
              versionHistory: releases.length > 0 ? releases : undefined
            }), isPrivatelyReviewableProduct && /*#__PURE__*/_jsx(ProductFeedbackCard, {
              partnerPortalId: partnerPortalId,
              logInUrl: logInUrl
            })]
          })
        }), compatibilityModalOpen && /*#__PURE__*/_jsx(PlanCombinationsModal, {
          features: filteredFeatures || [],
          onClose: () => setCompatibilityModalOpen(false)
        })]
      })
    })
  });
}